import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box, Drawer, IconButton, useMediaQuery } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Cookies from "js-cookie";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";
import { parseRoleList } from "./billing/billingFormat";
import PrimarySidebar, {
    PRIMARY_WIDTH_EXPANDED,
    PRIMARY_WIDTH_COLLAPSED,
} from "./Navbar/PrimarySidebar";
import SecondarySidebar, { SECONDARY_WIDTH } from "./Navbar/SecondarySidebar";
import { PRIMARY_ITEMS, resolvePrimaryItem } from "./Navbar/navConfig";

const SECONDARY_COLLAPSE_DURATION = 250;

const Navbar = ({ sidebarOpen, setSidebarOpen, children }) => {
    const { logout } = useAuth();
    const { isCreditBased } = useBilling();
    const [userRolesArray, setUserRolesArray] = useState([]);
    const [activePrimaryId, setActivePrimaryId] = useState(null);
    const [secondaryClosing, setSecondaryClosing] = useState(false);
    const isMobile = useMediaQuery("(max-width: 768px)");

    const isCollapsed = isMobile ? false : !sidebarOpen;

    const location = useLocation();

    useEffect(() => {
        const userRole = Cookies.get("userRole");
        if (userRole) {
            setUserRolesArray(parseRoleList(userRole));
        }
    }, []);

    useEffect(() => {
        const primary = PRIMARY_ITEMS
            .map((item) => resolvePrimaryItem(item, isCreditBased))
            .find(
                (item) =>
                    item.child &&
                    item.child.some(
                        (c) =>
                            c.path &&
                            (location.pathname === c.path ||
                                location.pathname.startsWith(c.path + "/"))
                    )
            );
        setActivePrimaryId(primary ? primary.id : null);
    }, [location.pathname, isCreditBased]);

    const activePrimary = activePrimaryId
        ? resolvePrimaryItem(PRIMARY_ITEMS.find((i) => i.id === activePrimaryId), isCreditBased)
        : null;

    const handlePrimaryClick = (item) => {
        if (!item.child) return;
        setActivePrimaryId((prev) => (prev === item.id ? null : item.id));
    };

    const handleSecondaryClose = () => {
        setSecondaryClosing(true);
        setTimeout(() => {
            setActivePrimaryId(null);
            setSecondaryClosing(false);
        }, SECONDARY_COLLAPSE_DURATION);
    };

    const handleLogout = () => {
        if (isMobile) setSidebarOpen(false);
        logout();
    };

    const sidebarContent = (
        <>
            <PrimarySidebar
                isCollapsed={isCollapsed}
                onToggle={() => setSidebarOpen((prev) => !prev)}
                activePrimaryId={activePrimaryId}
                onPrimaryClick={handlePrimaryClick}
                userRolesArray={userRolesArray}
                isCreditBased={isCreditBased}
                onLogout={handleLogout}
                isMobile={isMobile}
                onLinkClick={isMobile ? () => setSidebarOpen(false) : undefined}
            />
            {activePrimary?.child && (
                <SecondarySidebar
                    primaryItem={activePrimary}
                    userRolesArray={userRolesArray}
                    onClose={handleSecondaryClose}
                    onLinkClick={isMobile ? () => setSidebarOpen(false) : undefined}
                    isClosing={secondaryClosing}
                />
            )}
        </>
    );

    // Keep a single React tree for page content so crossing the mobile
    // breakpoint (e.g. opening DevTools) does not remount routes/editors.
    return (
        <Box
            sx={{
                display: "flex",
                height: "100vh",
                minHeight: "100vh",
                overflow: "hidden",
            }}
        >
            {isMobile ? (
                <>
                    <IconButton
                        onClick={() => setSidebarOpen(true)}
                        sx={{
                            position: "fixed",
                            top: 12,
                            left: 12,
                            zIndex: 1300,
                            color: "white",
                            backgroundColor: "primary.main",
                            "&:hover": { backgroundColor: "primary.dark" },
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Drawer
                        variant="temporary"
                        open={sidebarOpen}
                        onClose={() => setSidebarOpen(false)}
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            "& .MuiDrawer-paper": {
                                width: 320,
                                boxSizing: "border-box",
                            },
                        }}
                    >
                        <Box sx={{ display: "flex", height: "100%", overflow: "auto" }}>
                            {sidebarContent}
                        </Box>
                    </Drawer>
                </>
            ) : (
                sidebarContent
            )}

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default Navbar;
export { PRIMARY_WIDTH_EXPANDED, PRIMARY_WIDTH_COLLAPSED, SECONDARY_WIDTH };
